import { Injectable, Injector, Type } from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';

import { PopinModalRef } from './popin-modal.ref';
import { PopinModalComponent } from './popin-modal/popin-modal.component';
import { PopinModalConfig } from './popin-modal.config';
import { PopinComponentBase } from './popin-component-base';

@Injectable({
    providedIn: 'root'
})
export class PopinModalService
{
    private static defaultConfig = {
        width: 52,
        hasBackdrop: true
    } as PopinModalConfig;
    public overlayRef: OverlayRef = null;

    /** Popins ouvertes en mode « single », une par contenu : chacune a son exemplaire unique. */
    private singleRefs = new Map<Type<PopinComponentBase>, PopinModalRef>();

    constructor(private overlay: Overlay, private parentInjector: Injector)
    {
    }

    create (content: Type<PopinComponentBase>, config?: PopinModalConfig, data?: any): PopinModalRef
    {
        // Le config est reconstruit à chaque appel : un objet partagé, modifié par Object.assign, ferait
        // déborder sur les popins suivantes les options qui ne figurent pas dans defaultConfig.
        const mergedConfig = { ...PopinModalService.defaultConfig, ...config } as PopinModalConfig;

        if (mergedConfig.single && this.isSinglePopinOpen(content))
        {
            // Rien n'empêche un second clic avant la fin de l'animation d'ouverture, ni un clic répété
            // quand le bouton reste accessible derrière une popin sans backdrop : chaque appel créerait
            // son propre overlay, et l'ancien devenant inatteignable il faudrait fermer les volets un par
            // un. On renvoie donc la popin déjà ouverte, ce qui préserve la saisie en cours.
            return this.singleRefs.get(content);
        }

        this.overlayRef = this.overlay.create(this.getOverlayConfig(mergedConfig));
        const modalRef = new PopinModalRef(this.overlayRef, content, mergedConfig, data);
        const injector = this.createInjector(modalRef, this.parentInjector);

        this.overlayRef.attach(new ComponentPortal(PopinModalComponent, null, injector));

        if (mergedConfig.single)
        {
            this.singleRefs.set(content, modalRef);

            // detachments() émet quelle que soit la voie de fermeture - destroy(), dispose() ou destruction
            // par Angular - : la place est toujours rendue et le bouton redevient cliquable dès que le volet
            // a disparu, y compris quand la fermeture attend la fin de l'animation d'effacement.
            this.overlayRef.detachments().subscribe(() =>
            {
                if (this.singleRefs.get(content) === modalRef)
                {
                    this.singleRefs.delete(content);
                }
            });
        }

        return modalRef;
    }

    /** Une popin « single » de ce contenu est-elle ouverte et stable, c'est-à-dire dont la fermeture n'est pas engagée ? */
    private isSinglePopinOpen (content: Type<PopinComponentBase>): boolean
    {
        const opened = this.singleRefs.get(content);

        return !!opened && !opened.closing && opened.overlayRef.hasAttached();
    }

    createInjector (modalRef: PopinModalRef, injector: Injector)
    {
        const tokens = new WeakMap([[PopinModalRef, modalRef]]);

        return new PortalInjector(injector, tokens);
    }

    private getOverlayConfig (config: PopinModalConfig): OverlayConfig
    {
        return new OverlayConfig({
            width: `${config.width}vw`,
            height: '100vh',
            minWidth: `${config.width}vw`,
            minHeight: '100vh',
            hasBackdrop: config.hasBackdrop,
            backdropClass: 'popin-modal-backdrop',
            positionStrategy: this.getOverlayPosition(),
            scrollStrategy: this.overlay.scrollStrategies.reposition()
        });
    }

    private getOverlayPosition (): PositionStrategy
    {
        return this.overlay
            .position()
            .global()
            .right('0')
            .centerVertically();
    }
}
